// src/Hooks/useImportExcelListeners.js

import { useEffect } from 'react';
import { db } from '../Firebase/firebase';
import { collection, onSnapshot, writeBatch, doc, deleteField } from 'firebase/firestore';

export const useImportExcelListeners = (stateStore) => {
  const { state, setters } = stateStore;

  useEffect(() => {
    const unsubscribe = onSnapshot(
      collection(db, 'excelImportMenus'),
      (snapshot) => {
        const fetchedMenus = snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
        fetchedMenus.sort((a, b) => {
          const titleA = (a.title || '').toLowerCase();
          const titleB = (b.title || '').toLowerCase();
          if (titleA < titleB) return -1;
          if (titleA > titleB) return 1;
          return 0;
        });
        setters.setSavedMenus(fetchedMenus);
        setters.setIsLoadingMenus(false);
      },
      (error) => {
        console.error('Error fetching import menus:', error);
        setters.setIsLoadingMenus(false);
      },
    );
    return () => unsubscribe();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const unsubscribe = onSnapshot(
      collection(db, 'categories'),
      (snapshot) => {
        const fetchedCats = snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
        fetchedCats.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
        setters.setCategories(fetchedCats);
      },
      (error) => console.error('Error fetching categories:', error),
    );
    return () => unsubscribe();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const unsubscribe = onSnapshot(
      collection(db, 'region'),
      (snapshot) => {
        const fetchedRegions = snapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
        fetchedRegions.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
        setters.setRegions(fetchedRegions);
      },
      (error) => console.error('Error fetching regions:', error),
    );
    return () => unsubscribe();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const unsubscribe = onSnapshot(
      collection(db, 'markerIcons'),
      (snapshot) => {
        const fetchedIcons = snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
        fetchedIcons.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
        setters.setMarkerIcons(fetchedIcons);
      },
      (error) => console.error('Error fetching marker icons:', error),
    );
    return () => unsubscribe();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const unsubscribe = onSnapshot(
      doc(db, 'settings', 'TextFormatting'),
      (docSnap) => {
        if (docSnap.exists()) {
          const data = docSnap.data();
          setters.setMakeUppercase(
            data.makeUppercase === true || data.makeUppercase === 'true',
          );
        } else {
          setters.setMakeUppercase(false);
        }
      },
      (error) => console.error('Error fetching TextFormatting settings:', error),
    );
    return () => unsubscribe();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const unsubscribe = onSnapshot(
      doc(db, 'settings', 'RTEDefaultText'),
      (docSnap) => {
        if (docSnap.exists()) {
          const data = docSnap.data();
          if (data.FontType) setters.setRteFontType(data.FontType);
          if (data.FontSize) setters.setRteFontSize(data.FontSize);
        }
      },
      (error) => console.error('Error fetching RTEDefaultText settings:', error),
    );
    return () => unsubscribe();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const unsubscribe = onSnapshot(
      doc(db, 'settings', 'RTEDefaultHeading'),
      (docSnap) => {
        if (docSnap.exists()) {
          const data = docSnap.data();
          if (data.FontType) setters.setImHeadingFontType(data.FontType);
          if (data.FontSize) setters.setImHeadingFontSize(data.FontSize);
        }
      },
      (error) => console.error('Error fetching RTEDefaultHeading settings:', error),
    );
    return () => unsubscribe();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const unsubscribe = onSnapshot(
      doc(db, 'settings', 'MakePublic'),
      (docSnap) => {
        if (docSnap.exists()) {
          const data = docSnap.data();
          const days = parseInt(data.makePublicDefault, 10);
          if (!isNaN(days)) {
            const d = new Date();
            d.setDate(d.getDate() + days);
            const yyyy = d.getFullYear();
            const mm = String(d.getMonth() + 1).padStart(2, '0');
            const dd = String(d.getDate()).padStart(2, '0');
            setters.setDefaultPublicFromDate(`${yyyy}-${mm}-${dd}`);
          } else {
            setters.setDefaultPublicFromDate('');
          }
        } else {
          setters.setDefaultPublicFromDate('');
        }
      },
      (error) => console.error('Error fetching MakePublic settings:', error),
    );
    return () => unsubscribe();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const unsubscribeHistory = onSnapshot(
      collection(db, 'excelImports'),
      (snapshot) => {
        const historyData = snapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
        historyData.sort((a, b) => new Date(b.importDate) - new Date(a.importDate));
        setters.setImportHistory(historyData);
        setters.setIsLoadingHistory(false);

        setters.setSelectedHistoryIds((prev) =>
          prev.filter((id) => historyData.find((h) => h.id === id)),
        );
      },
      (error) => {
        console.error('Error fetching import history:', error);
        setters.setIsLoadingHistory(false);
      },
    );
    return () => unsubscribeHistory();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handlePasteMenu = async () => {
    const { copiedMenuTemplate } = state;
    if (!copiedMenuTemplate) return;

    setters.setIsProcessing(true);
    try {
      const batch = writeBatch(db);
      const newId = doc(collection(db, 'excelImportMenus')).id;
      const menuRef = doc(db, 'excelImportMenus', newId);

      const newMenuData = {
        title: copiedMenuTemplate.title
          ? `${copiedMenuTemplate.title} - Copy`
          : 'Untitled - Copy',
        mappings: copiedMenuTemplate.mappings || [],
        updatedAt: new Date().toISOString(),
      };

      if (!copiedMenuTemplate.mappings || copiedMenuTemplate.mappings.length === 0) {
        if (copiedMenuTemplate.rowMappings)
          newMenuData.rowMappings = copiedMenuTemplate.rowMappings;
        if (copiedMenuTemplate.rowSubMappings)
          newMenuData.rowSubMappings = copiedMenuTemplate.rowSubMappings;
        if (copiedMenuTemplate.rowStaticValues)
          newMenuData.rowStaticValues = copiedMenuTemplate.rowStaticValues;
        if (copiedMenuTemplate.customRows)
          newMenuData.customRows = copiedMenuTemplate.customRows;
      }

      setters.setLocalMenus((prev) => [{ id: newId, ...newMenuData }, ...prev]);
      setters.setSelectedMenuId(newId);

      batch.set(menuRef, newMenuData);
      await batch.commit();

      setters.setAlertState({
        open: true,
        message: 'Template pasted and saved.',
        severity: 'success',
      });
    } catch (err) {
      console.error('Error pasting menu:', err);
      setters.setAlertState({
        open: true,
        message: `Failed to paste menu: ${err.message}`,
        severity: 'error',
      });
    } finally {
      setters.setIsProcessing(false);
    }
  };

  const handleSaveMenu = async () => {
    const {
      localMenus,
      selectedMenuId,
      rowMappings,
      customRows,
      rowSubMappings,
      rowStaticValues,
    } = state;
    setters.setIsProcessing(true);
    try {
      const batch = writeBatch(db);

      localMenus.forEach((menu) => {
        const menuRef = doc(db, 'excelImportMenus', menu.id);
        let menuData = {
          title: menu.title || 'Untitled Menu',
          updatedAt: menu.updatedAt || new Date().toISOString(),
        };

        if (menu.id === selectedMenuId) {
          const mappings = [];
          const processedKeys = new Set();

          customRows.forEach((cRow) => {
            processedKeys.add(cRow.id);
            mappings.push({
              id: cRow.id,
              property: cRow.name,
              assignedTo: rowMappings[cRow.id] || '',
              selection: rowSubMappings[cRow.id] || '',
              value: rowStaticValues[cRow.id] || '',
              isCustom: true,
            });
          });

          Object.keys(rowMappings).forEach((key) => {
            if (!processedKeys.has(key)) {
              mappings.push({
                id: key,
                property: key.startsWith('prop_') ? key.substring(5) : '-',
                assignedTo: rowMappings[key] || '',
                selection: rowSubMappings[key] || '',
                value: rowStaticValues[key] || '',
                isCustom: true,
              });
            }
          });

          menuData.mappings = mappings;
          menuData.updatedAt = new Date().toISOString();

          menuData.rowMappings = deleteField();
          menuData.rowSubMappings = deleteField();
          menuData.rowStaticValues = deleteField();
          menuData.customRows = deleteField();
        } else {
          if (menu.mappings) menuData.mappings = menu.mappings;
        }

        batch.set(menuRef, menuData, { merge: true });
      });

      await batch.commit();
      setters.setIsMenuDirty(false);
      setters.setAlertState({
        open: true,
        message: 'Import template saved successfully.',
        severity: 'success',
      });
    } catch (err) {
      console.error('Error saving menus:', err);
      setters.setAlertState({
        open: true,
        message: `Failed to save menus: ${err.message}`,
        severity: 'error',
      });
    } finally {
      setters.setIsProcessing(false);
    }
  };

  return {
    handlePasteMenu,
    handleSaveMenu,
  };
};
